/**
 * CPF INDEX SERVICE — Padrão "Auxiliary Index Collection" (PASSO 19 / FASE 2)
 * Laudo de Engenharia: Seção 2.2 — Constraints de Unicidade em NoSQL
 *
 * Garante unicidade absoluta de CPFs via Batched Write atômico no Firestore.
 * A coleção `unique_cpfs` atua como índice auxiliar: doc ID = CPF sanitizado.
 *
 * Fluxo transacional:
 *   1. Tenta ler doc(db, 'unique_cpfs', cpfDigits)
 *   2. Se EXISTE → aborta (CPF já cadastrado)
 *   3. Se NÃO EXISTE → escreve cliente + índice no mesmo Batch atômico
 */

import {
  doc,
  getDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../shared/api/firebase/config';
import { FIREBASE_CONFIG } from '../config/Config';
import { FS } from '../shared/constants/firestorePaths';

const APP_ID = String(FIREBASE_CONFIG?.projectId || '').trim();

/**
 * Extrai dígitos numéricos de um CPF.
 * @param {string} cpf
 * @returns {string} 11 dígitos ou string vazia
 */
function sanitizeCpf(cpf) {
  return String(cpf || '').replace(/\D/g, '').slice(0, 11);
}

/**
 * Verifica se um CPF já possui índice registrado na coleção `unique_cpfs`.
 * Retorna o ID do cliente conflitante ou null.
 *
 * @param {string} cpf — CPF bruto (formatado ou limpo)
 * @returns {Promise<string|null>} clientId conflitante ou null
 */
export async function checkCpfIndexExists(cpf) {
  const digits = sanitizeCpf(cpf);
  if (digits.length !== 11) return null;

  try {
    const ref = doc(db, ...FS.uniqueCpfs(digits));
    const snap = await getDoc(ref);
    if (snap.exists()) {
      return snap.data()?.clientId || snap.id;
    }
    return null;
  } catch (err) {
    console.warn('[CPF_INDEX] Falha ao verificar índice:', err);
    return null;
  }
}

/**
 * Grava atomicamente um novo perfil de cliente E seu índice de CPF.
 * Usa writeBatch para garantir atomicidade (ACID): ou os dois documentos
 * são gravados, ou nenhum é.
 *
 * @param {import('firebase/firestore').DocumentReference} clientDocRef — ref do doc do cliente
 * @param {object} clientPayload — dados do cliente
 * @param {string} cpf — CPF do cliente (será sanitizado internamente)
 * @param {object} [options]
 * @param {string} [options.existingClientId] — se fornecido, pula criação do índice (update)
 * @returns {Promise<{success: boolean, error: string|null}>}
 */
export async function saveClientWithCpfIndex(clientDocRef, clientPayload, cpf, options = {}) {
  const digits = sanitizeCpf(cpf);
  const { existingClientId } = options;

  // Se não há CPF válido, salva sem índice (ex: PJ, Animal)
  if (digits.length !== 11) {
    try {
      const batch = writeBatch(db);
      batch.set(clientDocRef, { ...clientPayload, _updatedAt: serverTimestamp() }, { merge: true });
      await batch.commit();
      return { success: true, error: null };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }

  // Verifica conflito de CPF antes de gravar
  if (!existingClientId) {
    const conflict = await checkCpfIndexExists(digits);
    if (conflict && conflict !== clientDocRef.id) {
      return {
        success: false,
        error: `CPF ${digits} já registrado para o cliente ID: ${conflict}. Operação abortada (Conflict Scanner Tier 0).`,
        conflictId: conflict,
      };
    }
  }

  try {
    const batch = writeBatch(db);

    // 1. Grava (ou atualiza) o documento do cliente
    batch.set(clientDocRef, { ...clientPayload, _updatedAt: serverTimestamp() }, { merge: true });

    // 2. Registra (ou confirma) o índice de CPF — idempotente
    const cpfIndexRef = doc(db, ...FS.uniqueCpfs(digits));
    batch.set(cpfIndexRef, {
      clientId: clientDocRef.id,
      appId: APP_ID,
      indexedAt: serverTimestamp(),
    }, { merge: true });

    await batch.commit();
    return { success: true, error: null };
  } catch (err) {
    console.error('[CPF_INDEX] Falha no Batch Write:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Remove o índice de CPF ao deletar um cliente.
 * Deve ser chamado quando o cliente recebe isDeleted: true OU é removido definitivamente.
 *
 * @param {string} cpf
 * @returns {Promise<void>}
 */
export async function removeCpfIndex(cpf) {
  const digits = sanitizeCpf(cpf);
  if (digits.length !== 11) return;

  try {
    const batch = writeBatch(db);
    const cpfIndexRef = doc(db, ...FS.uniqueCpfs(digits));
    batch.delete(cpfIndexRef);
    await batch.commit();
    console.info(`[CPF_INDEX] Índice removido para CPF: ${digits}`);
  } catch (err) {
    console.warn('[CPF_INDEX] Falha ao remover índice:', err);
  }
}
